/**
 * Search and sort for the generic table.
 *
 * Extracted from core/components/v2/TableTemplate.tsx so it can be tested without
 * MUI or a renderer. Every table in the panel -- users, videos, workouts,
 * collections, library assets -- goes through these two functions, so a
 * regression here is visible everywhere at once and nowhere in particular.
 *
 * See tableUtils.test.ts.
 */

export type DataField<T> =
  | keyof T
  | {
      value: keyof T;
      formatter?: (value: any, row: T) => string;
      render?: (value: any, row: T) => React.ReactNode;
      sortOptions?: {
        sortValueExtractor?: (row: T) => string | number | Date | undefined;
        sortFieldName?: string;
        disabled?: boolean;
      };
    };

export type SortOptions<T> = {
  field: keyof T;
  fieldAlias?: string;
  direction: "asc" | "desc";
} | null;

export type SearchOptions<T> = {
  searchValue?: string;
  conditions?: ((row: T, searchValue: string) => boolean)[];
};

export const getDataField = <T,>(field: DataField<T>) => {
  if (typeof field === "object") {
    return field.value;
  }
  return field;
};

export const findDataField = <T,>(
  dataFields: DataField<T>[] | undefined,
  fieldName: keyof T
) => dataFields?.find((field) => getDataField(field) === fieldName);

export const findDataFieldByAlias = <T,>(
  dataFields: DataField<T>[] | undefined,
  alias: string
) =>
  dataFields?.find((field) => {
    if (typeof field === "object") {
      return field.sortOptions?.sortFieldName === alias;
    }
    return false;
  });

/**
 * Rows matching the current search.
 *
 * A row is kept when ANY condition matches, so the conditions are a set of
 * columns to search rather than a filter chain. An empty search value returns
 * everything.
 */
export const filterData = <T,>(
  data: T[] | undefined,
  searchOptions?: SearchOptions<T>
): T[] => {
  if (
    !searchOptions?.searchValue ||
    searchOptions.searchValue === "" ||
    !searchOptions?.conditions
  ) {
    return data || [];
  }
  return (
    data?.filter((row) =>
      searchOptions.conditions?.some((condition) =>
        condition(row, searchOptions.searchValue!)
      )
    ) || []
  );
};

/**
 * Rows in sort order.
 *
 * Returns a new array rather than sorting in place -- the version inside
 * TableTemplate mutated the caller's data, which meant the unsorted order was
 * lost after the first click on a column header.
 *
 * Values are compared by runtime type: strings with localeCompare, numbers
 * arithmetically, Dates by timestamp. `undefined` always sorts last regardless of
 * direction. Arrays and objects throw, deliberately -- a column configured to
 * sort on one is a configuration bug, and failing loudly beats a silently
 * arbitrary order.
 */
export const sortData = <T,>(
  unsortedData: T[],
  sort: SortOptions<T>,
  dataFields?: DataField<T>[]
): T[] => {
  if (!sort) {
    return unsortedData;
  }

  return [...unsortedData].sort((a, b) => {
    const dataField = sort.fieldAlias
      ? findDataFieldByAlias(dataFields, sort.fieldAlias)
      : findDataField(dataFields, sort.field);
    let extractValue:
      | ((row: T) => string | number | Date | undefined)
      | undefined
      | null = null;
    if (dataField && typeof dataField === "object") {
      extractValue = dataField.sortOptions?.sortValueExtractor;
    }
    const aValue = extractValue ? extractValue(a) : a[sort.field];
    const bValue = extractValue ? extractValue(b) : b[sort.field];

    if (aValue === undefined) return 1;
    if (bValue === undefined) return -1;

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sort.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (aValue instanceof Date && bValue instanceof Date) {
      return sort.direction === "asc"
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime();
    }

    if (Array.isArray(aValue) || Array.isArray(bValue)) {
      throw new Error("Sorting arrays is not supported");
    }

    if (typeof aValue === "object" || typeof bValue === "object") {
      throw new Error("Sorting objects is not supported");
    }

    return 0;
  });
};
